import React, { Fragment, ReactNode } from 'react';
import moment from 'moment-jalaali';
import { parse, ParsedQuery } from 'query-string';
import { RouteComponentProps } from '@reach/router';
import { MapDispatchToProps, connect, MapStateToProps } from 'react-redux';
import { IProviderItemView } from 'store/providers/providerTypes';
import { IUser } from 'store/users/usersType';
import { removeDuplicates } from 'utils/happyFunctions';
import { RootState } from 'store/store';
import protectedRoute from 'components/protectedRoute';
import PrintSingleTask from './PrintSingleTask';
import { IProject } from 'store/companies/companiesTypes';
import {
  requireProviderDocuments,
  requireProviderDocumentsFn,
} from 'store/providers/providerSlice';
import { ITaskPrintTemplateObject } from 'store/entity/IPrintTemplatePart';
import {
  PrintSingleTaskProvider,
  IPrintSingleTaskContext,
} from 'store/context/PrintSingleTaskContext';
import {
  setTotalProgress,
  setAllDataFetched,
  updateProgressCount,
  IProgress,
} from 'store/progress/ProgressSlice';
import messages from './Messages';
import ProgressBar from 'components/ProgressBar';
import { convertDigitToProperFormat } from 'utils/number';
import { appDispatch } from 'store/store';
import { ICompany } from 'store/companies/companiesTypes';
import { ITask } from 'store/tasks/tasksType';
import {
  getSingleCompany,
  getSingleCompanyFn,
} from 'store/companies/companiesSlice';
import {
  getChildrenTasks,
  getChildrenTasksFn,
  getSingleTask,
  getSingleTaskFn,
} from 'store/tasks/tasksSlice';
import { fetchAllUsers, fetchAllUsersFn } from 'store/users/usersSlice';
import { notifyError } from 'components/Notify/Notify';

type ParamProps = {
  tasksId: string;
  parentId: string;
  companyId: string;
};
type StateToProps = {
  parsed: ParsedQuery;
  progress: IProgress;
};
type DispatchToProps = {
  getCompany: (
    companyId: number,
    projectStub: boolean
  ) => ReturnType<typeof getSingleCompanyFn>;
  getSubTasks: (
    params: GetSubTasksParams
  ) => ReturnType<typeof getChildrenTasksFn>;
  getDocuments: (
    documentIds: number[]
  ) => ReturnType<typeof requireProviderDocumentsFn>;
  requireUsers: (userIds: number[]) => ReturnType<typeof fetchAllUsersFn>;
  requireSingleTask: (taskId: number) => ReturnType<typeof getSingleTaskFn>;
  setTotalProgress: (total: number) => void;
  setAllDataFetched: () => void;
  updateProgressCount: () => void;
};
type OwnProps = RouteComponentProps<ParamProps>;
type ComponentProps = OwnProps & StateToProps & DispatchToProps;
type ComponentStates = IPrintSingleTaskContext & {
  isLoading: boolean;
  error?: any;
  printTemplate?: ITaskPrintTemplateObject;
  printTaskIds?: Array<string>;
  parentTask?: ITask;
  renderParts: ReactNode[];
};

type GetSubTasksParams = {
  parentId: number;
  projectId: number;
  companyId: number;
};

class PrintSingleTaskContainer extends React.PureComponent<
  ComponentProps,
  ComponentStates
> {
  state: ComponentStates = {
    isLoading: true,
    company: undefined,
    parentTask: undefined,
    members: undefined,
    taskIds: undefined,
    allTasks: undefined,
    printTemplate: undefined,
    relatedProviderItems: undefined,
    printTaskIds: (this.props.tasksId || '').split(','),
    renderParts: [],
  };

  migratePrintTemplate = (
    template: ITaskPrintTemplateObject
  ): ITaskPrintTemplateObject => {
    const migratePart = (part: any) => {
      if (part.part === 'verifier') {
        part.part = 'verifiers';
      }
      return part;
    };

    return {
      header: template.header?.map(migratePart) || [],
      body:
        template.body?.map((item) => {
          const migrated = migratePart({ ...item });
          if (migrated.part === 'subTasks' && migrated.options?.tableBody) {
            migrated.options.tableBody =
              migrated.options.tableBody.map(migratePart);
          }
          if (migrated.part === 'subTasks' && migrated.options?.tableHeader) {
            migrated.options.tableHeader =
              migrated.options.tableHeader.map(migratePart);
          }
          return migrated;
        }) || [],
    };
  };

  componentDidMount() {
    this.fetchShareData();
  }

  async fetchShareData() {
    this.setState({ isLoading: true });
    this.props.setTotalProgress(6);
    const company = await this.props
      .getCompany(Number(this.props.companyId), false)
      .then((res) => res.company)
      .catch(() => null);
    if (!company) {
      this.setErrorMessage('company');
      return;
    }

    const parentTask = await this.props
      .requireSingleTask(Number(this.props.parentId))
      .then((res) => res.task)
      .catch(() => null);
    if (parentTask) {
      this.props.updateProgressCount();

      const subTasksTitle = parentTask.subTasksTitle;
      const selfProjectName = company.projects?.find(
        (it: IProject) => it.id === parentTask.projectId
      )?.name;
      const date = convertDigitToProperFormat(
        moment().format('jD jMMMM')
      ) as string;
      window.document.title = messages.getPrintTaskTitle(
        selfProjectName || '',
        date,
        subTasksTitle
      );
    }

    this.setState(
      {
        company,
      },
      () => {
        this.preRender.bind(this)();
      }
    );
  }

  fetchDataForPrintTask = async (selfTaskId: number) => {
    const {
      updateProgressCount: updateFetchCount,
      companyId,
      getSubTasks,
      getDocuments,
      requireSingleTask,
    } = this.props;
    try {
      const selfTask = await requireSingleTask(selfTaskId)
        .then((res) => res.task)
        .catch(() => null);
      if (!selfTask || !selfTask.printTemplate) {
        return false;
      }
      updateFetchCount();
      const params: GetSubTasksParams = {
        parentId: selfTask.id,
        projectId: selfTask.projectId,
        companyId: Number(companyId),
      };
      const subTasks = await getSubTasks(params)
        .then((res) => res.data)
        .catch(() => null);
      if (!subTasks) {
        return false;
      }
      updateFetchCount();

      const tasks = [selfTask, ...subTasks];
      const subTasksRelatedItems = this.getRelatedProviderItemIds(tasks);
      const relatedProviderItems = await getDocuments(subTasksRelatedItems)
        .then((res) => res.items)
        .catch(() => null);
      if (!relatedProviderItems) {
        return false;
      }
      updateFetchCount();
      if (selfTask.parentId) {
        const parentTask = await requireSingleTask(selfTask.parentId as number)
          .then((res) => {
            return res.task;
          })
          .catch(() => null);
        tasks.push(parentTask);
      }

      updateFetchCount();
      const ancestorId: number | undefined = selfTask.ancestors?.[1];
      let ancestorTask: ITask | null;
      if (ancestorId) {
        ancestorTask = await requireSingleTask(ancestorId as number)
          .then((res) => {
            return res.task;
          })
          .catch(() => null);
        tasks.push(ancestorTask);
      }
      updateFetchCount();

      let printTemplate: ITaskPrintTemplateObject;
      const template = this.state.company!.templates?.find(
        (it) => it.taskId === selfTask.templateId
      );
      try {
        const parsedTemplate = JSON.parse(template!.task!.printTemplate!);
        printTemplate = this.migratePrintTemplate(parsedTemplate);
      } catch {
        return false;
      }
      return {
        taskIds: {
          selfTask: selfTask.id,
          parentTask: selfTask.parentId,
          ancestorTask: ancestorId,
        },
        allTasks: tasks,
        relatedProviderItems,
        printTemplate,
      };
    } catch (e) {
      notifyError(e);
      return false;
    }
  };

  setErrorMessage = (message: any) => {
    this.setState({ error: message });
  };

  getTaskMembers = (companyMembers: ICompany['members']): number[] => {
    const memberIds = [];
    memberIds.push(...Object.values(companyMembers).map((m) => m.userId));
    return removeDuplicates(memberIds);
  };

  getRelatedProviderItemIds = (subTasks: ITask[]) => {
    const itemIds = subTasks.reduce((list: number[], task: ITask) => {
      task.relatedProviderItems?.map((item) => list.push(item.id));
      return list;
    }, []);
    return removeDuplicates(itemIds);
  };

  async preRender() {
    const ids = (this.props.tasksId || '')
      .split(',')
      .map((itm) => Number(itm))
      .filter((item) => item > 0);

    this.props.setTotalProgress(4 * ids.length + 1);
    const renderPartsData: Array<{
      members: Array<IUser>;
      taskIds: {
        selfTask: number;
        parentTask: number;
        ancestorTask: number | undefined;
      };
      allTasks: ITask[];
      relatedProviderItems: IProviderItemView[];
      printTemplate: ITaskPrintTemplateObject;
      task: ITask;
    }> = [];

    const memberIds = this.getTaskMembers(this.state.company!.members!);
    const members = (await this.props.requireUsers(memberIds).catch(() => null))
      ?.users;
    this.props.updateProgressCount();

    const waitList: Promise<any>[] = [];

    ids.forEach((id, index) => {
      renderPartsData.push(null as any);
      waitList.push(
        this.fetchDataForPrintTask(id).then((res) => {
          if (res)
            renderPartsData[index] = {
              members,
              ...res,
              task: res.allTasks.find(
                (itm) => itm.id === res.taskIds.selfTask
              )!,
            };
        })
      );
    });
    await Promise.allSettled(waitList);

    const renderParts = renderPartsData
      .filter((e) => !!e)
      .sort((a, b) => {
        if (a.task.order === b.task.order) {
          return a.task.title.localeCompare(b.task.title);
        } else {
          return a.task.order - b.task.order;
        }
      })
      .map((itm) => {
        const { printTemplate, task, ...rest } = itm;
        return (
          <div className={'pagebreak'} key={`${task.title}${task.order}`}>
            <PrintSingleTaskProvider
              value={{
                company: this.state.company,
                ...rest,
              }}
            >
              <PrintSingleTask printTemplate={printTemplate} />
            </PrintSingleTaskProvider>
          </div>
        );
      });

    this.setState({
      renderParts,
      isLoading: false,
    });

    this.props.setAllDataFetched();
  }

  render() {
    const { progress } = this.props;
    return progress.allDataFetched && !this.state.isLoading ? (
      this.state.renderParts.length === 0 ? (
        <div className="w-screen h-screen flex items-center justify-center">
          There is no task with print template
        </div>
      ) : (
        <div>
          {this.state.renderParts.map((part, idx) => (
            <Fragment key={idx}>{part}</Fragment>
          ))}
        </div>
      )
    ) : (
      <div>
        <ProgressBar count={progress.count} total={progress.total} />
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<
  StateToProps,
  OwnProps & RouteComponentProps,
  RootState
> = (state, props) => {
  let parsed: ParsedQuery = {};
  if (props.location) {
    parsed = parse(props.location.search);
  }
  const progress = state.progress;
  return { progress, parsed };
};

const mapDispatchToProps: MapDispatchToProps<
  DispatchToProps,
  OwnProps
> = () => {
  return {
    getCompany: (companyId: number, projectStub: boolean) =>
      appDispatch(getSingleCompany({ companyId, projectStub })),
    getSubTasks: (params: GetSubTasksParams) =>
      appDispatch(
        getChildrenTasks({
          parentId: params.parentId,
          projectId: params.projectId,
          companyId: params.companyId,
        })
      ),
    getDocuments: (itemIds: number[]) =>
      appDispatch(requireProviderDocuments({ documentIds: itemIds })),
    requireUsers: (userList: number[]) =>
      appDispatch(fetchAllUsers({ usersIds: userList })),
    requireSingleTask: (taskId: number) =>
      appDispatch(getSingleTask({ taskId })),
    setTotalProgress: (total: number) => appDispatch(setTotalProgress(total)),
    setAllDataFetched: () => appDispatch(setAllDataFetched()),
    updateProgressCount: () => appDispatch(updateProgressCount()),
  };
};

export default protectedRoute(
  connect(mapStateToProps, mapDispatchToProps)(PrintSingleTaskContainer)
);
